import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { Wordmark } from "./Logo";
import { scrollToId } from "./smooth";
import { PHONE, PHONE_LABEL, WHATSAPP_URL } from "@/lib/api";
import { WhatsAppIcon } from "./WhatsApp";
import { LangSwitch } from "./LangSwitch";
import { useT } from "@/lib/i18n";

const TXT = {
  no: { links: ["Tjenester", "Chiptuning", "ECU / TCU", "Bilnøkler", "Rogaland"], prices: "Priser", top: "RB TUN AS – til toppen", menu: "Meny", wa: "Send melding på WhatsApp" },
  en: { links: ["Services", "Chiptuning", "ECU / TCU", "Car keys", "Rogaland"], prices: "Prices", top: "RB TUN AS – back to top", menu: "Menu", wa: "Message us on WhatsApp" },
};
const IDS = ["tjenester", "tuning", "ecu", "bilnokler", "rogaland"];
const PAGES = { tuning: "/chiptuning", bilnokler: "/bilnokler" };

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const t = useT(TXT);
  const LINKS = IDS.map((id, i) => ({ id, label: t.links[i], page: PAGES[id] }));
  const go = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(id);
  };
  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-[#08090b]/70 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <a href="/" data-testid="nav-logo" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0 }); }} aria-label={t.top}>
          <Wordmark />
        </a>
        <nav className="hidden items-center gap-5 md:flex lg:gap-9">
          {LINKS.map((l) => l.page ? (
            <Link key={l.id} to={l.page} data-testid={`nav-link-${l.id}`}
              className="group relative whitespace-nowrap font-mono text-xs uppercase tracking-[0.2em] text-zinc-300 transition-colors hover:text-white">
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[var(--red)] transition-[width] duration-300 group-hover:w-full" />
            </Link>
          ) : (
            <a key={l.id} href={`/#${l.id}`} data-testid={`nav-link-${l.id}`} onClick={go(l.id)}
              className="group relative whitespace-nowrap font-mono text-xs uppercase tracking-[0.2em] text-zinc-300 transition-colors hover:text-white">
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[var(--red)] transition-[width] duration-300 group-hover:w-full" />
            </a>
          ))}
          <Link to="/priser" data-testid="nav-link-priser"
            className="group relative whitespace-nowrap font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors hover:text-white">
            {t.prices}
            <span className="absolute -bottom-1 left-0 h-px w-full bg-[var(--red)]" />
          </Link>
        </nav>
        <div className="flex items-center gap-3">
          <LangSwitch testid="nav-lang-switch" />
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-whatsapp-link"
            className="hidden items-center gap-2 bg-[#25D366] px-4 py-2.5 font-mono text-xs font-bold uppercase tracking-widest text-[#08090b] transition-transform hover:-translate-y-0.5 sm:flex md:hidden lg:flex">
            <WhatsAppIcon size={15} /> WhatsApp
          </a>
          <a href={`tel:${PHONE}`} data-testid="nav-phone-link"
            className="hidden items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-xs font-bold uppercase tracking-widest text-white transition-colors hover:border-white/50 lg:flex">
            <Phone size={14} /> {PHONE_LABEL}
          </a>
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className="p-2 md:hidden" aria-label={t.menu}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }}
            className="overflow-hidden border-t border-white/5 md:hidden" data-testid="nav-mobile-menu">
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((l) => l.page ? (
                <Link key={l.id} to={l.page} data-testid={`mobile-nav-link-${l.id}`} onClick={() => setOpen(false)}
                  className="py-3 text-left font-display text-3xl font-bold uppercase">{l.label}</Link>
              ) : (
                <a key={l.id} href={`/#${l.id}`} data-testid={`mobile-nav-link-${l.id}`} onClick={go(l.id)}
                  className="py-3 text-left font-display text-3xl font-bold uppercase">{l.label}</a>
              ))}
              <Link to="/priser" data-testid="mobile-nav-link-priser" onClick={() => setOpen(false)}
                className="py-3 text-left font-display text-3xl font-bold uppercase text-[var(--red)]">{t.prices}</Link>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="mobile-nav-whatsapp"
                className="mt-2 flex items-center gap-2 bg-[#25D366] px-4 py-3.5 font-mono text-sm font-bold text-[#08090b]">
                <WhatsAppIcon size={16} /> {t.wa}
              </a>
              <a href={`tel:${PHONE}`} data-testid="mobile-nav-phone"
                className="flex items-center gap-2 border border-white/15 px-4 py-3.5 font-mono text-sm font-bold text-white">
                <Phone size={16} /> {PHONE_LABEL}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
